import { createHash, randomBytes } from 'crypto';
import { KeyValStore } from '../app/utils/KeyValStore';

export interface PKCECodeChallenge {
  codeChallenge: string;
  codeChallengeMethod: string;
}

/**
 * Refer to RFC 7636: Proof Key for Code Exchange by OAuth Public Clients
 * https://tools.ietf.org/html/rfc7636
 */
const CODE_CHALLENGE_METHOD = 'S256'; // RFC 7636, section 4.2
const HASH_ALGORITHM = 'sha256';
const NUM_RANDOM_BYTES = 32; // RFC 7636, section 7.1

const CODE_VERIFIER_STORAGE_KEY = 'PKCE.codeVerifier';
const KNOWN_APP_URI_PROTOCOLS_STORAGE_KEY = 'PKCE.knownAppUriProtocols';

const KNOWN_APP_URI_PROTOCOLS = [ 'tableau', 'tableauauth' ];

const store = new KeyValStore('vizportal', 'pkce');

/**
 * Generate code challenge and store its verifier.
 */
async function generateCodeChallenge(): Promise<PKCECodeChallenge> {
  const codeVerifier = randomBytes(NUM_RANDOM_BYTES);
  const codeChallenge = createHash(HASH_ALGORITHM)
    .update(codeVerifier)
    .digest();

  await store.set(CODE_VERIFIER_STORAGE_KEY, urlEncode(codeVerifier));

  return {
    codeChallenge: urlEncode(codeChallenge),
    codeChallengeMethod: CODE_CHALLENGE_METHOD
  };
}

/**
 * Perform URL-safe Base64 encoding on given value and return the encoded value.
 */
function urlEncode(value: Buffer): string {
  return value.toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function addKnownAppUriProtocol(value: string): Promise<void> {
  return getKnownAppUriProtocols()
    .then(stored => {
      if (!stored) {
        return setKnownAppUriProtocols([ value ]);
      } else if (!stored.includes(value)) {
        return setKnownAppUriProtocols([ ...stored, value ]);
      }
    });
}

function isAppUriProtocolKnown(value: string): Promise<boolean> {
  if (KNOWN_APP_URI_PROTOCOLS.includes(value)) {
    return Promise.resolve(true);
  }
  return getKnownAppUriProtocols()
    .then(stored => stored && stored.includes(value));
}

function getCodeVerifier(): Promise<string> {
  return store.get(CODE_VERIFIER_STORAGE_KEY);
}

function getKnownAppUriProtocols(): Promise<string[]> {
  return store.get(KNOWN_APP_URI_PROTOCOLS_STORAGE_KEY) || Promise.resolve([]);
}

function setKnownAppUriProtocols(protocols: string[]): Promise<void> {
  return store.set(KNOWN_APP_URI_PROTOCOLS_STORAGE_KEY, protocols);
}

export const PKCEStore = {
  addKnownAppUriProtocol,
  isAppUriProtocolKnown,
  generateCodeChallenge,
  getCodeVerifier,
  getKnownAppUriProtocols,
  setKnownAppUriProtocols,
};
